// components/sections/PropertySection.js
import React from 'react';
import InteractiveField from '../common/InteractiveField';
import MultilineField from '../common/MultilineField';
import { getHandoverActNumber } from '../../utils/attachmentNumbers';

/**
 * Компонент для раздела "1. Предмет Договора".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 * @param {object} contractData - Все данные договора для вычисления номеров приложений.
 */
const PropertySection = ({ data, onChange, showValidation = false, contractData }) => {
  const {
    roomCount = '',
    totalArea = '',
    includeLivingArea = false,
    livingArea = '',
    address = '',
    buildingFloors = '',
    apartmentFloor = '',
    ownershipConfirmation = { type: 'egrn', details: '' },
    occupancy = { type: 'alone', members: [{ id: 1, nameAndDob: '' }] },
    includeCadastralNumber = false,
    cadastralNumber = '',
    includeOtherParameters = false,
    otherParameters = ''
  } = data;

  const handleFieldChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleToggleChange = (field) => {
    onChange({ ...data, [field]: !data[field] });
  };

  const handleOwnershipTypeChange = (newType) => {
    onChange({
      ...data,
      ownershipConfirmation: {
        type: newType,
        details: ''
      }
    });
  };

  const handleOwnershipDetailsChange = (value) => {
    onChange({
      ...data,
      ownershipConfirmation: {
        ...data.ownershipConfirmation,
        details: value
      }
    });
  };

  const handleOccupancyTypeChange = (newType) => {
    const newOccupancy = { ...occupancy, type: newType };
    if (newType === 'family' && (!newOccupancy.members || newOccupancy.members.length === 0)) {
      newOccupancy.members = [{ id: Date.now(), nameAndDob: '' }];
    }
    onChange({ ...data, occupancy: newOccupancy });
  };

  const handleMemberChange = (index, value) => {
    const newMembers = [...occupancy.members];
    newMembers[index].nameAndDob = value;
    onChange({ ...data, occupancy: { ...occupancy, members: newMembers } });
  };

  const handleAddMember = () => {
    const newMembers = [...occupancy.members, { id: Date.now(), nameAndDob: '' }];
    onChange({ ...data, occupancy: { ...occupancy, members: newMembers } });
  };

  const handleRemoveMember = (index) => {
    if (occupancy.members.length <= 1) return;
    const newMembers = occupancy.members.filter((_, i) => i !== index);
    onChange({ ...data, occupancy: { ...occupancy, members: newMembers } });
  };

  // ✨ Инициализация счетчика для динамической нумерации
  let clauseNumber = 4;

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        1. Предмет Договора
      </h2>

      <p className="document-preamble">
        <b>1.1.</b> Наймодатель обязуется предоставить Нанимателю во владение и пользование для проживания изолированное жилое помещение, квартиру (далее — Помещение), а Наниматель обязуется принять и своевременно вносить плату.
      </p>

      <p className="document-preamble">
        <b>1.2.</b> Помещение располагается в многоквартирном доме по адресу: <InteractiveField value={address} onChange={(v) => handleFieldChange('address', v)} placeholder="г. Москва, ул. Центральная, д. 10, кв. 55" isRequired={true} showValidation={showValidation} validationFieldId="property-address" />, в <InteractiveField value={buildingFloors} onChange={(v) => handleFieldChange('buildingFloors', v)} placeholder="5" type="number" isRequired={true} showValidation={showValidation} validationFieldId="property-buildingFloors" />-этажном доме на <InteractiveField value={apartmentFloor} onChange={(v) => handleFieldChange('apartmentFloor', v)} placeholder="3" type="number" isRequired={true} showValidation={showValidation} validationFieldId="property-apartmentFloor" />-м этаже.
      </p>

      <p className="document-preamble">
        Параметры Помещения:
      </p>
      <p className="document-preamble">
        квартира: <InteractiveField value={roomCount} onChange={(v) => handleFieldChange('roomCount', v)} placeholder="2" type="number" isRequired={true} showValidation={showValidation} validationFieldId="property-roomCount" />-комнатная;
      </p>
      <p className="document-preamble">
        общая площадь <InteractiveField value={totalArea} onChange={(v) => handleFieldChange('totalArea', v)} placeholder="55.5" type="number" isRequired={true} showValidation={showValidation} validationFieldId="property-totalArea" /> кв. м;
      </p>

      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeLivingArea ? 'selected' : ''}`}
            onClick={() => handleToggleChange('includeLivingArea')}
          >
            {includeLivingArea ? '✓ Жилая площадь' : '+ Добавить жилую площадь'}
          </button>
        </div>
        {includeLivingArea && (
          <p className="document-preamble">
            жилая площадь <InteractiveField value={livingArea} onChange={(v) => handleFieldChange('livingArea', v)} placeholder="35.0" type="number" isRequired={true} showValidation={showValidation} /> кв. м;
          </p>
        )}
      </div>

      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeCadastralNumber ? 'selected' : ''}`}
            onClick={() => handleToggleChange('includeCadastralNumber')}
          >
            {includeCadastralNumber ? '✓ Кадастровый номер' : '+ Добавить кадастровый номер'}
          </button>
        </div>
        {includeCadastralNumber && (
          <p className="document-preamble">
            кадастровый номер квартиры: <InteractiveField value={cadastralNumber} onChange={(v) => handleFieldChange('cadastralNumber', v)} placeholder="77:01:0001001:1234" isRequired={true} showValidation={showValidation} />;
          </p>
        )}
      </div>

      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeOtherParameters ? 'selected' : ''}`}
            onClick={() => handleToggleChange('includeOtherParameters')}
          >
            {includeOtherParameters ? '✓ Иные параметры' : '+ Добавить иные параметры'}
          </button>
        </div>
        {includeOtherParameters && (
          <p className="document-preamble">
            иные параметры помещения: <MultilineField value={otherParameters} onChange={(v) => handleFieldChange('otherParameters', v)} placeholder="пропишите дополнительные параметры помещения" isRequired={true} minRows={1} maxRows={5} />
          </p>
        )}
      </div>

      <div className="configurable-section dynamic-content-block active-purple">
        <p className="document-preamble">
          <b>1.3.</b> Наймодатель подтверждает право собственности:
        </p>
        
        <p className="document-preamble">
          заверением, что является собственником Помещения{ownershipConfirmation.type === 'egrn' && (
            <> и выпиской из Единого государственного реестра недвижимости выданной Росреестром <InteractiveField value={ownershipConfirmation.details} onChange={handleOwnershipDetailsChange} placeholder="№99/2021/123456789 от 01.09.2025" isRequired={true} showValidation={showValidation} validationFieldId="property-egrnExtract" /></>
          )}{ownershipConfirmation.type === 'document' && (
            <> и <InteractiveField value={ownershipConfirmation.details} onChange={handleOwnershipDetailsChange} placeholder="свидетельством о гос. регистрации №123 от 01.09.2025" isRequired={true} showValidation={showValidation} validationFieldId="property-ownershipDocument" /></>
          )}.
        </p>

        <div className="option-pills-group">
          <button
            className={`option-pill ${ownershipConfirmation.type === 'assurance' ? 'selected' : ''}`}
            onClick={() => handleOwnershipTypeChange('assurance')}
          >
            Заверением
          </button>
          <button
            className={`option-pill ${ownershipConfirmation.type === 'egrn' ? 'selected' : ''}`}
            onClick={() => handleOwnershipTypeChange('egrn')}
          >
            Выпиской из ЕГРН
          </button>
          <button
            className={`option-pill ${ownershipConfirmation.type === 'document' ? 'selected' : ''}`}
            onClick={() => handleOwnershipTypeChange('document')}
          >
            Документом о праве собственности
          </button>
        </div>
      </div>


      <p className="document-preamble">
        <b>1.4.</b> Наймодатель заверяет, что указанное Помещение свободное, не обременено правами третьих лиц, не заложено, не является предметом спора, не состоит под арестом, и отсутствуют какие-либо иные ограничения по распоряжению им.
      </p>

      {/* Единый блок для связанных пунктов с непрерывной полоской */}
      <div className="dynamic-content-block active-purple">
        <p className="document-preamble">
          <b>1.{++clauseNumber}.</b> Наниматель заверяет, что вселяется в Помещение {occupancy.type === 'alone' ? 'единолично.' : 'вместе с лицами:'}
        </p>

        {occupancy.type === 'family' && (
          <div className="members-list">
            {occupancy.members.map((member, index) => (
              <div key={member.id} className="member-item">
                <span className="member-bullet">-</span>
                <InteractiveField
                  value={member.nameAndDob}
                  onChange={(value) => handleMemberChange(index, value)}
                  placeholder="Ф.И.О., дата рождения"
                  isRequired={true}
                />
                <button
                  onClick={() => handleRemoveMember(index)}
                  className="remove-member-btn"
                  disabled={occupancy.members.length <= 1}
                  aria-label="Удалить"
                >
                  ×
                </button>
              </div>
            ))}
            <button onClick={handleAddMember} className="add-member-btn">
              + Добавить лицо
            </button>
          </div>
        )}

        <div className="option-pills-group" style={{ justifyContent: 'flex-start', marginTop: '16px', marginBottom: '16px' }}>
          <button
            className={`option-pill ${occupancy.type === 'alone' ? 'selected' : ''}`}
            onClick={() => handleOccupancyTypeChange('alone')}
          >
            Единолично
          </button>
          <button
            className={`option-pill ${occupancy.type === 'family' ? 'selected' : ''}`}
            onClick={() => handleOccupancyTypeChange('family')}
          >
            Совместно с лицами
          </button>
        </div>

        {/* Условные пункты, которые появляются только при выборе "Совместно с лицами" */}
        {occupancy.type === 'family' && (
          <>
            <p className="document-preamble">
              <b>1.{++clauseNumber}.</b> Граждане, постоянно проживающие совместно с Нанимателем, имеют равные с ним права по пользованию Помещением.
            </p>
            <p className="document-preamble">
              <b>1.{++clauseNumber}.</b> Наниматель несет ответственность перед Наймодателем за действия граждан, постоянно проживающих совместно с ним, которые нарушают условия настоящего Договора.
            </p>
          </>
        )}
      </div>

      {/* Пункт про Акт приема-передачи - безусловный, но с правильной нумерацией */}
      {(() => {
        const handoverActNumber = getHandoverActNumber();
        return (
          <p className="document-preamble">
            <b>1.{++clauseNumber}.</b> Состояние Помещения, оборудования и передаваемого имущества, а также их перечень, указывается в Акте приема-передачи Помещения (Приложение № {handoverActNumber}).
          </p>
        );
      })()}
    </div>
  );
};

export default PropertySection;